"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useCollection } from "@/features/collections/useCollections";
import { apiFetch } from "@/lib/api";
import type { Item } from "@/types";

/**
 * One item by id.
 *
 * No `transformProperties` step, unlike the legacy hook: the backend resolves each
 * field's translation map against `?lang=` before serializing, so properties
 * arrive as plain scalars in the requested language (`items.lang.ts`).
 *
 * `keepPreviousData` holds the previously opened POI on screen while the next one
 * loads, so the details panel doesn't flash empty between selections — but only
 * while there *is* a selection. Applied unconditionally it also survives the
 * selection being cleared, which left the details panel open over a URL that no
 * longer named a POI, with no way back to the results.
 */
export function itemQueryKey(
  projectId: string | undefined,
  collectionId: string | undefined,
  itemId: string | null | undefined,
  lang: string
) {
  return ["item", { projectId, collectionId, itemId, lang }] as const;
}

export function useItem(itemId: string | null | undefined) {
  const collection = useCollection();
  const lang = useLocale();
  const projectId = collection?.projectId;
  const collectionId = collection?.id;

  return useQuery({
    queryKey: itemQueryKey(projectId, collectionId, itemId, lang),
    queryFn: ({ signal }) =>
      apiFetch<Item>(
        `/projects/${projectId}/collections/${collectionId}/items/${itemId}`,
        { lang, signal }
      ),
    enabled: !!projectId && !!collectionId && !!itemId,
    placeholderData: itemId ? keepPreviousData : undefined,
  });
}
